import { useQueryClient, useMutation } from '@tanstack/vue-query';
import inviteManagementApi, { type Invite, type InviteTenant } from '@api/inviteManagement';
import type { Ref, ComputedRef } from 'vue';

export interface Tenant {
    id: number;
    slug: string;
    name: string;
    logo: string | null;
    accent: string | null;
    allow_support: boolean;
    google_analytics_measurement_id: string | null;
    default_company_id: number | null;
    roles: string[];
    role_labels?: string[];
    title?: string | null;
}

interface SelectTenantOptions {
    fetchNotifications?: boolean;
    closeModal?: () => void;
}

interface RollbackContext {
    previousInvites: Invite[];
    previousOptimistic: InviteTenant[];
}

export function useTenantSelection() {
    const userStore = useUserStore();
    const router = useRouter();
    const queryClient = useQueryClient();

    const switchingToTenantId: Ref<number | null> = ref(null);
    const pendingInvites: Ref<Invite[]> = ref([]);
    const loadingInvites: Ref<boolean> = ref(true);
    const optimisticTenants: Ref<InviteTenant[]> = ref([]);

    const storeTenants: ComputedRef<Tenant[]> = computed(() => userStore.data?.tenants || []);
    const pendingInvitesCount: ComputedRef<number> = computed(() => userStore.data?.pending_invites_count || 0);

    const tenants = computed<(Tenant | InviteTenant)[]>(() => {
        const realIds = new Set(storeTenants.value.map((t) => t.id));
        const pending = optimisticTenants.value.filter((t) => !realIds.has(t.id));
        return [...storeTenants.value, ...pending];
    });

    const joiningTenantIds = computed<number[]>(() => optimisticTenants.value.map((t) => t.id));

    async function fetchPendingInvites(): Promise<void> {
        loadingInvites.value = true;
        try {
            const response = await inviteManagementApi.getPendingInvites();
            pendingInvites.value = response.data;
        } catch (error) {
            console.error('Failed to fetch pending invites:', error);
            pendingInvites.value = [];
        } finally {
            loadingInvites.value = false;
        }
    }

    async function selectTenant(
        tenant: Tenant | InviteTenant,
        { fetchNotifications = true, closeModal }: SelectTenantOptions = {},
    ): Promise<void> {
        switchingToTenantId.value = tenant.id;

        queryClient.cancelQueries();
        queryClient.clear();

        if (userStore.tenantId !== tenant.id) {
            await userStore.setTenant(tenant.id, fetchNotifications);
        }
        userStore.tenantUserSelected = true;

        closeModal?.();

        await nextTick();

        if (userStore.isRestrictedFieldStaff()) {
            router.push({ name: 'Home' });
        } else {
            router.push({ name: 'Dashboard' });
        }
    }

    const acceptMutation = useMutation<void, Error, Invite, RollbackContext>({
        mutationFn: (invite) => inviteManagementApi.acceptInvite(invite.id),
        onMutate: (invite) => {
            const previousInvites = [...pendingInvites.value];
            const previousOptimistic = [...optimisticTenants.value];

            pendingInvites.value = pendingInvites.value.filter((i) => i.id !== invite.id);

            if (invite.tenant) {
                optimisticTenants.value.push({ ...invite.tenant });
            }

            return { previousInvites, previousOptimistic };
        },
        onSuccess: async () => {
            await userStore.fetchUser();
            optimisticTenants.value = [];
            await fetchPendingInvites();
        },
        onError: (_error, _invite, context) => {
            if (context) {
                pendingInvites.value = context.previousInvites;
                optimisticTenants.value = context.previousOptimistic;
            }
        },
    });

    const leaveMutation = useMutation<void, Error, Tenant | InviteTenant, { previousTenants: Tenant[] }>({
        mutationFn: (tenant) => inviteManagementApi.leaveTenant(tenant.id),
        onMutate: (tenant) => {
            const previousTenants = [...(userStore.data?.tenants || [])];

            if (userStore.data) {
                userStore.data.tenants = userStore.data.tenants.filter((t: Tenant) => t.id !== tenant.id);
            }

            return { previousTenants };
        },
        onSuccess: async (_data, tenant) => {
            const wasCurrentTenant = userStore.tenantId === tenant.id;
            await userStore.fetchUser(false);

            if (wasCurrentTenant) {
                queryClient.clear();
                userStore.hideTenantModal();
                router.push({ name: 'SelectTenant' });
            }
        },
        onError: (_error, _tenant, context) => {
            if (context && userStore.data) {
                userStore.data.tenants = context.previousTenants;
            }
        },
    });

    const declineMutation = useMutation<void, Error, Invite, { previousInvites: Invite[]; previousCount: number }>({
        mutationFn: (invite) => inviteManagementApi.declineInvite(invite.id),
        onMutate: (invite) => {
            const previousInvites = [...pendingInvites.value];
            const previousCount = userStore.data?.pending_invites_count || 0;
            pendingInvites.value = pendingInvites.value.filter((i) => i.id !== invite.id);
            if (userStore.data) {
                userStore.data.pending_invites_count = Math.max(0, previousCount - 1);
            }
            return { previousInvites, previousCount };
        },
        onSuccess: async () => {
            await userStore.refreshTenants();
        },
        onError: (_error, _invite, context) => {
            if (context) {
                pendingInvites.value = context.previousInvites;
                if (userStore.data) {
                    userStore.data.pending_invites_count = context.previousCount;
                }
            }
        },
    });

    const acceptingId = computed<number | null>(() =>
        acceptMutation.isPending.value ? (acceptMutation.variables.value?.tenant?.id ?? null) : null,
    );
    const decliningId = computed<number | null>(() =>
        declineMutation.isPending.value ? (declineMutation.variables.value?.tenant?.id ?? null) : null,
    );
    const leavingTenantId = computed<number | null>(() =>
        leaveMutation.isPending.value ? (leaveMutation.variables.value?.id ?? null) : null,
    );

    return {
        tenants,
        pendingInvites,
        pendingInvitesCount,
        loadingInvites,
        switchingToTenantId,
        joiningTenantIds,
        acceptingId,
        decliningId,
        leavingTenantId,
        fetchPendingInvites,
        selectTenant,
        acceptInvite: (invite: Invite) => acceptMutation.mutate(invite),
        declineInvite: (invite: Invite) => declineMutation.mutate(invite),
        leaveTenant: (tenant: Tenant | InviteTenant) => leaveMutation.mutate(tenant),
    };
}
